import { ArrowLeft, ArrowRight, Save, Loader2 } from 'lucide-react';
import { Button } from '@/client/components/ui/button';
import { cn } from '@/client/lib/utils';
import { useWizard } from './wizard-context';
import type { WizardNavigationProps } from './types';

export function WizardNavigation({
  nextButtonText = 'Continue',
  backButtonText = 'Back',
  completeButtonText = 'Complete',
  showSaveDraft = true,
  onSaveDraft,
  onComplete,
  onCancel,
  isNextDisabled = false,
  className,
  variant = 'footer',
}: WizardNavigationProps) {
  const { isFirstStep, isLastStep, isSubmitting, nextStep, prevStep, setSubmitting } = useWizard();

  const handleNext = async () => {
    if (isLastStep) {
      setSubmitting(true);
      try {
        await onComplete();
      } finally {
        setSubmitting(false);
      }
    } else {
      await nextStep();
    }
  };

  const handleSaveDraft = async () => {
    if (!onSaveDraft) return;
    setSubmitting(true);
    try {
      await onSaveDraft();
    } finally {
      setSubmitting(false);
    }
  };

  // Header variant - compact inline buttons
  if (variant === 'header') {
    return (
      <div className={cn('flex items-center gap-2', className)}>
        {/* Save Draft - only show if callback provided */}
        {showSaveDraft && onSaveDraft && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={handleSaveDraft}
            disabled={isSubmitting}
            className="gap-1.5 hidden sm:flex"
          >
            <Save className="h-3.5 w-3.5" />
            <span className="hidden md:inline">Save Draft</span>
          </Button>
        )}

        {/* Back button - step navigation */}
        {!isFirstStep && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={prevStep}
            disabled={isSubmitting}
            className="gap-1.5"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">{backButtonText}</span>
          </Button>
        )}

        {/* Next / Complete button */}
        <Button
          type="button"
          size="sm"
          onClick={handleNext}
          disabled={isSubmitting || isNextDisabled}
          className={cn('gap-1.5', isLastStep && 'bg-primary hover:bg-primary/90')}
        >
          {isSubmitting ? (
            <>
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              <span className="hidden sm:inline">{isLastStep ? 'Publishing…' : 'Saving…'}</span>
            </>
          ) : isLastStep ? (
            <>{completeButtonText}</>
          ) : (
            <>
              {nextButtonText}
              <ArrowRight className="h-3.5 w-3.5" />
            </>
          )}
        </Button>
      </div>
    );
  }

  // Footer variant - full width with borders (original design)
  return (
    <div
      className={cn('flex items-center justify-between border-t bg-muted/30 px-6 py-4', className)}
    >
      {/* Left side - Save Draft */}
      <div>
        {showSaveDraft && onSaveDraft && (
          <Button
            type="button"
            variant="ghost"
            onClick={handleSaveDraft}
            disabled={isSubmitting}
            className="gap-2"
          >
            <Save className="h-4 w-4" />
            Save Draft
          </Button>
        )}
      </div>

      {/* Right side - Navigation */}
      <div className="flex items-center gap-3">
        {/* Cancel button */}
        {onCancel && (
          <Button type="button" variant="ghost" onClick={onCancel} disabled={isSubmitting}>
            Cancel
          </Button>
        )}

        {/* Back button */}
        {!isFirstStep && (
          <Button
            type="button"
            variant="outline"
            onClick={prevStep}
            disabled={isSubmitting}
            className="gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            {backButtonText}
          </Button>
        )}

        {/* Next / Complete button */}
        <Button
          type="button"
          onClick={handleNext}
          disabled={isSubmitting || isNextDisabled}
          className={cn('gap-2 min-w-[120px]', isLastStep && 'bg-primary hover:bg-primary/90')}
        >
          {isSubmitting ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              {isLastStep ? 'Publishing…' : 'Saving…'}
            </>
          ) : isLastStep ? (
            <>{completeButtonText}</>
          ) : (
            <>
              {nextButtonText}
              <ArrowRight className="h-4 w-4" />
            </>
          )}
        </Button>
      </div>
    </div>
  );
}
